import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { authService } from '@/domains/internal/auth';
import { logger } from '@/utils/logger';

interface AuthContextType {
  isAuthReady: boolean;
  isAuthenticated: boolean;
  refreshAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const checkAuth = useCallback(async () => {
    try {
      logger.debug('[AuthContext] Checking authentication...');

      // Check if user is authenticated
      const authenticated = authService.isAuthenticated();

      if (authenticated) {
        // Ensure user data is loaded
        const user = await authService.getCurrentUser();

        if (user) {
          // Get user ID based on user type
          const getUserId = () => {
            if ('staff_id' in user) return user.staff_id;
            if ('team_member_id' in user) return user.team_member_id;
            if ('referral_source_id' in user) return user.referral_source_id;
            return 'unknown';
          };

          logger.debug('[AuthContext] Authentication verified, user loaded:', {
            userId: getUserId(),
            userType: user.user_type
          });
          setIsAuthenticated(true);
        } else {
          logger.warn('[AuthContext] No user data found despite authenticated state');
          setIsAuthenticated(false);
        }
      } else {
        logger.debug('[AuthContext] User not authenticated');
        setIsAuthenticated(false);
      }
    } catch (error) {
      logger.error('[AuthContext] Error during auth check:', error);
      setIsAuthenticated(false);
    }
  }, []);

  const refreshAuth = useCallback(async () => {
    logger.debug('[AuthContext] Refreshing auth state...');
    await checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    const initializeAuth = async () => {
      await checkAuth();
      // Mark auth as ready after initial check
      logger.debug('[AuthContext] Auth initialization complete, marking as ready');
      setIsAuthReady(true);
    };

    initializeAuth();

    // Listen for storage events (login/logout from other tabs)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'access_token' || e.key === 'user') {
        logger.debug('[AuthContext] Storage change detected, refreshing auth...');
        checkAuth();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [checkAuth]);

  return (
    <AuthContext.Provider value={{ isAuthReady, isAuthenticated, refreshAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
